<script setup>
import { onMounted, watch, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { initFlowbite } from 'flowbite'
import Pusher from 'pusher-js'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useBatchStore } from '@/stores/BatchStore'
import { useUserStore } from './stores/UserStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import MainLayout from '@/components/MainLayout/index.vue'
import GlobalVideoWidget from '@/components/GlobalVideoWidget.vue'
import NewVersionToast from '@/components/NewVersionToast.vue'
import ListingsExportToast from '@/components/ListingsExportToast.vue'
import * as Sentry from "@sentry/browser"
import { COLLX_CHANNEL } from '@/constants'
import { isMobile } from '@/helpers/general'

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const batchStore = useBatchStore()
const connectionStore = useConnectionStore()

const isAuthLayout = computed(() => !route.meta?.requireAuth)

const user = computed(() => userStore.user)

useKeyboardShortcut(router)

globalStore.fetchBuildTxt()

setInterval(() => {
  globalStore.fetchBuildTxt()
}, 300000)

onMounted(async () => {
  globalStore.windowWidth = window.innerWidth
  globalStore.windowHeight = window.innerHeight
  window.addEventListener('resize', () => {
    globalStore.windowWidth = window.innerWidth
    globalStore.windowHeight = window.innerHeight

    if (window.innerWidth < 1280) {
      globalStore.sidebar = false
    }
  })

  // initialize components based on data attribute selectors
  initFlowbite();

  const params = new URLSearchParams(window.location.search)
  const authAs = params.get('authAs')
  const sessionOverrideAuthToken = sessionStorage.getItem('cdp_auth_override_token')

  if (authAs || sessionOverrideAuthToken) {
    await userStore.checkSession(authAs || sessionOverrideAuthToken)
    console.log('logged in as', userStore.user?.email)
    router.replace('/')
  } else {
    userStore.checkSession()
  }

  if (navigator.userAgent.search('Mac') !== -1) {
    globalStore.userAgent = 'cmd'
  } else {
    globalStore.userAgent = 'ctrl'
  }
  globalStore.sidebar =  JSON.parse(localStorage.getItem('sidebar') || false)
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
})

watch(user, () => {
  userStore.user && batchStore.fetchConfigs({ withoutLoader: true })
  userStore.user && Sentry.setUser(userStore.user)
})

watch(
  () => userStore.loggedIn,
  (loggedIn, prevLoggedIn) => {
    if (!prevLoggedIn && loggedIn) {
      // ...
      // Enable pusher logging - don't include this in production
      Pusher.logToConsole = true;

      var pusher = new Pusher('f1107eef8ca40861ee35', {
        cluster: 'mt1'
      });

      var channel = pusher.subscribe(`channel-${userStore.user?.id}`);

      channel.bind('batchUpdate', function(data) {     
        if (
          data?.Event === 'card_processing_completed' || 
          data?.Event === 'imort_cert_processing_completed' || 
          data?.Event === 'imort_cert_processing_failed' || 
          data?.Event === 'card_processing_failed' || 
          data?.Event === 'rotate_processing_completed'
        ) {
          batchStore.updateCard(data.CardId, data?.Event)
        }

        if (data?.Event === 'replace_card_processing_completed') {
          batchStore.updateCard(data.CardId, data?.Event)
          setTimeout(() => {
            batchStore.replaceImageLoader = false
          }, 0)
        }

        if ((data?.Event === 'remove_banner_completed' || data?.Event === 'add_banner_completed') && data.LastCard) {
          batchStore.fetchSelectedBatchCards(null, false)
        }

        if (data?.Event === 'swap_processing_completed') {
          batchStore.updateCard(data.CardId, data?.Event)

          if (data.LastCard) {
            batchStore.updateSwappedToast(true, 'Image swap complete!')
          }
        }

        if (data?.Event === 'card_reprocessing_completed' && data.LastCard) {
          batchStore.fetchSelectedBatchCards(null, false)
        }

        if (data?.Event === 'scan_counts_updated') {
          batchStore.config = {
            ...batchStore.config,
            availableScans: data.AvailableScans,
            totalScans: data.TotalScans
          }
        }

        if (data?.Event === 'card_uploaded_to_cloud_storage' && data?.MobileUpload) {
          const filesNames = data?.UploadFilenames && JSON.parse(data?.UploadFilenames)
          batchStore.uploads = [
            ...batchStore.uploads, 
            { 
              front: { name: filesNames.frontFilename || '' }, 
              back: { name: filesNames.backFilename || '' }, 
              thumbnail: null, 
              thumbnailFront: data.Images.FrontSmImage || null,
              thumbnailBack: data.Images.BackSmImage || null,
              progress: 0,
              pending: false, 
              uploaded: true,
              cardId: data.CardId
            }
          ]
          batchStore.countOfUploadedImagesFromMobile = batchStore.countOfUploadedImagesFromMobile + 1
        }
      })

      channel.bind('collx_import_completed', function(data) {
        connectionStore.showExportToast = true
        connectionStore.exportedChannelInfo = {...COLLX_CHANNEL, label: 'Listings published', message: 'cards have been listed on CollX.', ...data}
      
        if (route.params.batchId) {
          batchStore.fetchSelectedBatchCards(null, false)
        }
      })

      channel.bind('otp_login_completed', function(data) {
        if (!isMobile()) {
          router.push(`/batches/${route.params.batchId}/add/upload`)
          userStore.otpLoginDone = true
        }
      })

      channel.bind('selected_image_side', function(data) {
        if (!isMobile()) {
          router.replace(`${route.path}?sides=${data.side}`)
        }
      })

      channel.bind('otp_login_ended', function(data) {
        if (isMobile()) {
          router.push(`/mobile-scan/session-ended`)
        }
      })
    }
  }
)

watch(route, () => {
  if (route.path === '/batches') {
    zE('webWidget', 'updateSettings', {
      webWidget: {
        offset: { horizontal: '75px', vertical: '55px' }
      }
    })

    zE('webWidget', 'show')
  } else if (['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted'].includes(route.name)) {
    zE('webWidget', 'hide')
  } else {
    zE('webWidget', 'updateSettings', {
      webWidget: {
        offset: { horizontal: '5px', vertical: '5px' }
      }
    })

    zE('webWidget', 'show')
  }
})

// Global error handler
window.onerror = function (message, source, lineno, colno, error) {
  if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message,
      source,
      lineno,
      colno,
      error
    });
  }
  // Return false to prevent the default handling of the error
  return false;
};

// Event listener for global errors
window.addEventListener('error', function (event) {
  if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected:', {
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  }
});

// Event listener for unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    console.error('ResizeObserver loop error detected (unhandled rejection):', event.reason);
  }
});
</script>

<template> 
  <!-- <RouterView v-if="isAuthLayout || batchStore.config?.subscription?.plan_id" /> -->
  <NewVersionToast />
  <ListingsExportToast />

  <RouterView v-if="isAuthLayout || ['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted'].includes(route.name)" />
  <template v-else>
    <MainLayout />

    <GlobalVideoWidget />
  </template>
</template>